import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import './EmailVerification.css';
import { resendVerificationCode, verifyUserEmail } from '../../api/user/auth';
import LogoWhite from '../../assets/logo white.png';
import LogoBlack from '../../assets/logo black.jpg';
import { useTheme } from '../../context/ThemeContext';

const OTP_LENGTH = 6;

const UserEmailVerification = () => {
  const { id } = useParams();
  const { theme } = useTheme();
  const Logo = theme === 'dark' ? LogoWhite : LogoBlack;
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  // Le code vient d'être envoyé (arrivée depuis register/login) : on laisse
  // un délai de 60s avant de permettre un renvoi, pour éviter les spam.
  const [countdown, setCountdown] = useState(60);
  const [shake, setShake] = useState(false);
  const inputsRef = useRef([]);
  const navigate = useNavigate();
  const redirectTimer = useRef(null);

  // Nettoyage du timer de redirection si le composant se démonte
  useEffect(() => {
    return () => clearTimeout(redirectTimer.current);
  }, []);

  // Compte à rebours avant renvoi du code
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const verificationCode = otp.join('');
  const isComplete = verificationCode.length === OTP_LENGTH;

  // Saisie : chaque case accepte 1 chiffre puis avance automatiquement
  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otp];
    next[index] = digit;
    setOtp(next);
    setError('');
    if (digit && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  // Backspace sur case vide → retour case précédente ; flèches gauche/droite
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  // Coller un code entier (ex : 123456)
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((digit, i) => {
      next[i] = digit;
    });
    setOtp(next);
    setError('');
    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const handleVerifyCode = async () => {
    if (!isComplete) {
      setError('Please enter the complete 6-digit code');
      triggerShake();
      return;
    }

    try {
      setIsVerifying(true);
      setError('');
      setMessage('');

      const response = await verifyUserEmail(id, verificationCode);
      setMessage(response.message || 'Email verified successfully! Redirecting to setup...');
      redirectTimer.current = setTimeout(() => navigate('/onboarding-intent'), 1200);
    } catch (error) {
      setError(error.message || 'Failed to verify code. Please check the code and try again.');
      setOtp(Array(OTP_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
      triggerShake();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setIsSending(true);
      setError('');
      setMessage('');

      const response = await resendVerificationCode(id);
      setMessage(response.message || 'A new verification code has been sent to your email');
      setCountdown(60);
      setOtp(Array(OTP_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
    } catch (error) {
      setError(error.message || 'Failed to resend verification code');
    } finally {
      setIsSending(false);
    }
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="email-verification-container">
      <div className={`email-verification-card ${shake ? 'shake' : ''}`}>
        <div className="verification-topbar" aria-hidden="true">
          <span className="topbar-green" />
          <span className="topbar-red" />
          <span className="topbar-yellow" />
        </div>

        <div className="verification-brand">
          <img src={Logo} alt="Spotfli" className="verification-logo" />
        </div>

        <div className="verification-icon">
          <i className="fa fa-envelope-open-o" aria-hidden="true" />
        </div>

        <div className="verification-header">
          <h1>Email Verification</h1>
          <p>Verify your email address to unlock all Spotfli features</p>
        </div>

        <div className="verification-content">
          <div className="code-verification-section">
            <div className="otp-label">
              <span>Enter the 6-digit code</span>
            </div>

            <div className="otp-inputs" role="group" aria-label="6-digit verification code">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputsRef.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  value={digit}
                  maxLength={2}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  aria-label={`Digit ${index + 1}`}
                  className="otp-input"
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <div className="code-hint">
              <i className="fa fa-paper-plane-o" aria-hidden="true" />
              We&apos;ve sent a code to your email — it expires in 15 minutes
            </div>
          </div>

          <div className="code-actions">
            <button
              onClick={handleVerifyCode}
              disabled={isVerifying || !isComplete}
              className="verify-code-btn"
            >
              {isVerifying ? (
                <>
                  <span className="button-spinner" aria-hidden="true" />
                  Verifying...
                </>
              ) : (
                <>
                  <i className="fa fa-check" aria-hidden="true" />
                  Verify Email
                </>
              )}
            </button>

            <button
              onClick={handleResendCode}
              disabled={countdown > 0 || isSending}
              className="resend-btn"
            >
              {isSending ? (
                <>
                  <span className="button-spinner light" aria-hidden="true" />
                  Sending...
                </>
              ) : countdown > 0 ? (
                <>
                  <i className="fa fa-clock-o" aria-hidden="true" />
                  Resend in {formatCountdown(countdown)}
                </>
              ) : (
                <>
                  <i className="fa fa-redo" aria-hidden="true" />
                  Resend Code
                </>
              )}
            </button>
          </div>

          {message && (
            <div className="success-message" role="status">
              <i className="fa fa-check-circle" aria-hidden="true" />
              {message}
            </div>
          )}
          {error && (
            <div className="error-message" role="alert">
              <i className="fa fa-exclamation-circle" aria-hidden="true" />
              {error}
            </div>
          )}

          <div className="verification-footer">
            <Link to="/login" className="back-link">
              <i className="fa fa-arrow-left" aria-hidden="true" />
              Back to Login
            </Link>
            <span className="secure-note">
              <i className="fa fa-lock" aria-hidden="true" />
              Secure verification
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserEmailVerification;
